import { Axios } from "api/axios";
import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import SearchBox from "components/SearchBox/SearchBox";
import { app_modules } from "helpers/apps_modules_list";
import { useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import { exportCustomerCSV, fetchAllCustomers } from "slices/Customer/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";

import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ActionButton from "components/Buttons/ActionButton";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import StatusToggle from "components/StatusToggle";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";

const Customer = () => {
  document.title = "Customer List";
  const dispatch = useDispatch<any>();
  const location = useLocation();

  // Redux selector for category state
  const selectCategorys = createSelector(
    (state: any) => state.customer,
    (category) => ({
      customers: category.customerList,
      rowCount: category.rowCount,
      loading: category.loading,
      error: category.error,
    })
  );

  const { customers, rowCount, loading, error } = useSelector(selectCategorys);

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.customer
  );

  // Use preserved table state hook
  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = usePreservedTableState({
    key: "customer",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const navigate = useNavigate();

  let data = customers || [];

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  useEffect(() => {
    dispatch(
      fetchAllCustomers({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, pageIndex, pageSize, sorting, search, filterValue]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch]);

  const handleStatusToggle = async (customer: any) => {
    try {
      const updatedStatus = !customer.is_active;
      const updatedCustomer = {
        id: customer.customer_id,
        is_active: updatedStatus,
      };
      const payload = { is_active: updatedCustomer.is_active };
      setIsStatusUpdateLoading(true);
      await Axios.patch(
        `/master/customer/${updatedCustomer.id}/update/`,
        payload
      );
      toast.success(
        `Customer ${updatedStatus ? "Activated" : "Deactivated"} Successfully`
      );

      dispatch(
        fetchAllCustomers({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      );
    } catch (err: any) {
      toast.error("Failed to update status");
    } finally {
      setIsStatusUpdateLoading(false);
    }
  };

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { blob, filename } = await exportCustomerCSV({
        search,
        filters: filterValue,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      console.error("Export failed", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export CSV";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  // Menu items configuration for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: EditIcon,
        label: "Edit",
        permission: canUpdate,
        onClick: (customer: any) => {
          navigate(`/edit-customer/${customer.customer_id || customer.id}`, {
            state: {
              tab: "customer",
              customer_id: customer.customer_id || customer.id,
            },
          });
        },
        color: "primary",
      },
      {
        icon: VisibilityIcon,
        label: "View Details",
        permission: canView,
        onClick: (customer: any) => {
          navigate(`/customer-details/${customer.customer_id || customer.id}`, {
            state: {
              tab: "customerDetails",
              customer_id: customer.customer_id || customer.id,
            },
          });
        },
        color: "info",
      },
    ];
    return items;
  }, [canUpdate, canView, navigate]);

  // Dynamic action column using DynamicMenuItem
  const DynamicAction = useMemo(() => {
    const hasPermission = canUpdate || canView;

    if (!hasPermission) return [];

    return [
      {
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          stickyRight: true,
        },
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
    // : [];
  }, [menuItems, canUpdate, canView]);

  const columns = [
    {
      header: "Sr. No.",
      id: "sr_no",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }: { row: any }) =>
        row.original.sr_no || row.index + 1 + pageIndex * pageSize || "-",
    },
    {
      header: "Customer Name",
      accessorKey: "customer_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      Rnet: "customer_name", //*// This is important
      filterFetchUrl: "/master/customer/dropdown/customer_name",
    },
    {
      header: "Company Name",
      accessorKey: "company_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      Rnet: "company_name", //*// This is important
      filterFetchUrl: "/master/customer/dropdown/company_name",
      cell: ({ row }: { row: any }) => row.original.company_name || "-",
    },
    {
      header: "Customer Type",
      accessorKey: "customer_type",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      Rnet: "customer_type", //*// This is important
      filterFetchUrl: "/master/customer/dropdown/customer_type",
      maxSize: 60,
      cell: ({ row }: { row: any }) => {
        return (
          <span className="text-capitalize">
            {row.original.customer_type || "-"}
          </span>
        );
      },
    },
    {
      header: "Email",
      accessorKey: "email",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => row.original.email || "-",
    },
    {
      header: "Contact Number",
      accessorKey: "contact_number",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => row.original.contact_number || "-",
    },
    {
      header: "Aadhar No.",
      accessorKey: "aadhar_no",
      enableColumnFilter: true,
      filterType: "dropdown" as const,
      filterFetchUrl: "/master/customer/dropdown/aadhar_no",
      enableSorting: true,
      cell: ({ row }: { row: any }) => row.original.aadhar_no || "-",
    },
    {
      header: "GSTIN",
      accessorKey: "gstin",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      Rnet: "gstin", //*// This is important
      filterFetchUrl: "/master/customer/dropdown/gstin",
      cell: ({ row }: { row: any }) => row.original.gstin || "-",
    },
    {
      header: "Status",
      accessorKey: "is_active",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }: { row: any }) => {
        const customer = row.original;
        return (
          <StatusToggle
            isActive={customer.is_active}
            onChange={() => {
              handleStatusToggle(customer);
            }}
            disabled={!canUpdate}
          />
        );
      },
    },
    {
      header: "Contact Person Name",
      accessorKey: "contact_person_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      Rnet: "contact_person_name", //*// This is important
      filterFetchUrl: "/master/customer/dropdown/contact_person_name",
      cell: ({ row }: { row: any }) => row.original.contact_person_name || "-",
    },
    {
      header: "Contact Person Mobile No",
      accessorKey: "contact_person_mobile_no",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) =>
        row.original.contact_person_mobile_no || "-",
    },
    {
      header: "Address",
      accessorKey: "address",
      enableColumnFilter: false,
      enableSorting: false,
      // maxSize: 350,
      cell: ({ row }: { row: any }) => (
        <TruncatedTextCell
          text={row?.original?.address}
          maxWidth="350px"
          maxLines={4}
          rowIndex={row.index}
          tooltipId={`address-tooltip-${row.index}`}
        />
      ),
    },
    {
      header: "State",
      accessorKey: "state",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      Rnet: "state", //*// This is important
      filterFetchUrl: "/master/customer/dropdown/state",
      cell: ({ row }: { row: any }) => row.original.state || "-",
    },
    {
      header: "City",
      accessorKey: "city",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      Rnet: "city", //*// This is important
      filterFetchUrl: "/master/customer/dropdown/city",
      cell: ({ row }: { row: any }) => row.original.city || "-",
    },
    {
      header: "Country",
      accessorKey: "country",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      Rnet: "country", //*// This is important
      filterFetchUrl: "/master/customer/dropdown/country",
      cell: ({ row }: { row: any }) => row.original.country || "-",
    },
    {
      header: "Pincode",
      accessorKey: "pincode",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => row.original.pincode || "-",
    },

    {
      header: "Created By/Date",
      accessorKey: "created_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => {
        const name = row.original.created_by_user?.username ?? "N/A";
        const date = row.original.created_at
          ? new Date(row.original.created_at).toISOString().split("T")[0]
          : "N/A";
        return `${name} ${date}`;
      },
    },
    {
      header: "Updated By/Date",
      accessorKey: "updated_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => {
        const name = row.original.updated_by_user?.username ?? "N/A";
        const date = row.original.updated_at
          ? new Date(row.original.updated_at).toISOString().split("T")[0]
          : "N/A";
        return `${name} ${date}`;
      },
    },

    ...DynamicAction,
  ];

  return (
    <ListingPageContainer>
      <style>
        {`
          .truncated-text-cell-wrapper {
            min-width: 0 !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.5em !important;
          }
        `}
      </style>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Customer</h6>
                </Col>

                {/* Search Input Box (reusable)*/}
                <Col xl={2} md={3}>
                  <SearchBox value={searchInput} onChange={setSearchInput} />
                </Col>
                <Col className="col-md-auto">
                  <div className="hstack gap-2">
                    {canView && (
                      <ActionButton
                        label="Export"
                        icon="bi bi-box-arrow-up"
                        handleClick={handleExport}
                        variant="light"
                      />
                    )}
                    {canCreate && (
                      <ActionButton
                        label="Add Customer"
                        icon="bi bi-plus-circle"
                        navigateLink="/add-customer"
                        variant="primary"
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper
                loading={loading || isStatusUpdateLoading}
                error={error}
              >
                <TableContainer
                  columns={columns}
                  data={data}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search customers..."
                  manualSorting={true}
                  colValue={setFilterValue}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default Customer;
